/**
 * Theme selection — design/09 §9.
 *
 *   "Dark mode is SELECTED, not computed … No automatic inversion, no filter hacks.
 *    Both modes are first-class, both are validated against their own surface, and
 *    the theme toggle wins over the OS setting in both directions."
 *
 * "In both directions" is the load-bearing clause and the part usually got wrong: a
 * user on a light OS who chooses dark must get dark, AND a user on a dark OS who
 * chooses light must get light. That rules out the common `prefers-color-scheme`-as-
 * base pattern, where an explicit light choice is indistinguishable from no choice.
 * So the store holds three states, not two, and only `system` consults the OS.
 *
 * design/09 §9 also states the reason this is not a preference: "Planners work long
 * shifts, frequently in warehouses and back-offices with poor lighting."
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Theme } from '../tokens/generated/tokens';

export const THEME_CHOICES = ['light', 'dark', 'system'] as const;
export type ThemeChoice = (typeof THEME_CHOICES)[number];

const STORAGE_KEY = 'bata.theme';

function systemTheme(): Theme {
  if (typeof globalThis.matchMedia !== 'function') return 'light';
  return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeState {
  readonly choice: ThemeChoice;
  readonly resolved: Theme;
  setChoice: (choice: ThemeChoice) => void;
  /** Called by the OS-preference listener; only has an effect when choice is
   *  `system`, which is what "the toggle wins over the OS setting" means. */
  syncSystem: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      choice: 'system',
      resolved: systemTheme(),
      setChoice: (choice) => {
        const resolved = choice === 'system' ? systemTheme() : choice;
        set({ choice, resolved });
        applyTheme(choice, resolved);
      },
      syncSystem: () => {
        if (get().choice !== 'system') return;
        const resolved = systemTheme();
        set({ resolved });
        applyTheme('system', resolved);
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ choice: state.choice }),
      /*
       * THE CHOICE AND WHAT IT RESOLVES TO ARE RESTORED TOGETHER.
       *
       * Only `choice` is persisted, and the default merge laid it over the initial state —
       * whose `resolved` was computed from the OS before anything was read. So a planner who
       * had chosen Dark reloaded into choice 'dark', resolved 'light'. `onRehydrateStorage`
       * below set the page's attribute from `choice`, so every CSS surface went dark; the
       * charts read `resolved` (they cannot use a custom property, `src/charts/theme.ts`),
       * so they drew with the light theme on a dark page — audit v4 G-26, on the most common
       * path there is: choose Dark once, come back tomorrow.
       *
       * Recomputed here, at the merge, so there is no moment in which the two disagree.
       */
      merge: (persisted, current) => {
        const stored = (persisted as { readonly choice?: unknown } | undefined)?.choice;
        const choice: ThemeChoice = (THEME_CHOICES as readonly unknown[]).includes(stored)
          ? stored as ThemeChoice
          : current.choice;
        return { ...current, choice, resolved: choice === 'system' ? systemTheme() : choice };
      },
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const resolved = state.choice === 'system' ? systemTheme() : state.choice;
        applyTheme(state.choice, resolved);
      },
    },
  ),
);

/**
 * Writes the attribute the generated stylesheet keys off. `data-theme` is ABSENT
 * for `system`, which is what lets `:root:not([data-theme])` pick up the media
 * query — and present for both explicit choices, which is what lets an explicit
 * light choice beat a dark OS.
 */
export function applyTheme(choice: ThemeChoice, resolved: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
  root.dataset['resolvedTheme'] = resolved;
}

export function watchSystemTheme(): () => void {
  if (typeof globalThis.matchMedia !== 'function') return () => {};
  const query = globalThis.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => useThemeStore.getState().syncSystem();
  query.addEventListener('change', handler);
  return () => query.removeEventListener('change', handler);
}

export const useTheme = (): Theme => useThemeStore((s) => s.resolved);
export const useThemeChoice = (): ThemeChoice => useThemeStore((s) => s.choice);
